import { AnalysisHistoryList } from "@/components/analysis/analysis-history-list";
import { EmailRouting } from "@/components/analysis/email-routing";
import { Mailbox } from "@/components/analysis/mailbox";
import { Navigate, useLoaderData } from "@tanstack/react-router";
import { useState } from "react";

/**
 * `/agents/analyst/email` — admin-only email source. Tabs: Routing (sender→project
 * mapping), Mailbox (recent messages + run analysis by hand), History
 * (analysed emails). New suggestions land in the AI Suggestions queue.
 *
 * E2E behaviour contract lives in `e2e/analysis-email.spec.ts`.
 */

const TABS = [
  { key: "routing", label: "Routing" },
  { key: "mailbox", label: "Mailbox" },
  { key: "history", label: "History" },
] as const;
type Tab = (typeof TABS)[number]["key"];

export function EmailPage() {
  const { user } = useLoaderData({ from: "/_app" });
  const [tab, setTab] = useState<Tab>("routing");

  if (user.globalRole !== "admin") return <Navigate to="/" />;

  return (
    <div className="w-full space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Email</h2>
        <p className="text-muted-foreground mt-1">
          Forward client emails to the watched mailbox; the AI proposes follow-ups into the
          Suggestions queue.
        </p>
      </div>

      <div className="flex gap-1 border-b">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            data-testid={`email-tab-${t.key}`}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
              tab === t.key
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "routing" && <EmailRouting />}
      {tab === "mailbox" && <Mailbox />}
      {tab === "history" && (
        <AnalysisHistoryList
          source="email"
          emptyLabel="No analysed emails yet. Forward a client email to the watched mailbox."
        />
      )}
    </div>
  );
}
